import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MessageSquare, Send, ArrowLeft, User, LogIn, Edit2, Trash2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, API, getAuthHeaders } from '@/App';
import { toast } from 'sonner';

// Keys for localStorage persistence
const DRAFT_KEY = 'uniconnect_dm_drafts';
const SELECTED_CONV_KEY = 'uniconnect_selected_conversation';

// Get all drafts
const getDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}');
  } catch {
    return {};
  }
};

// Save draft for a user
const saveDraft = (userId, content) => {
  const drafts = getDrafts();
  if (content) {
    drafts[userId] = content;
  } else {
    delete drafts[userId];
  }
  localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
};

// Get draft for a user
const getDraft = (userId) => {
  return getDrafts()[userId] || '';
};

// Save selected conversation
const saveSelectedConversation = (userId, userName) => {
  if (userId && userName) {
    localStorage.setItem(SELECTED_CONV_KEY, JSON.stringify({ userId, userName }));
  } else {
    localStorage.removeItem(SELECTED_CONV_KEY);
  }
};

// Get selected conversation
const getSelectedConversation = () => {
  try {
    return JSON.parse(localStorage.getItem(SELECTED_CONV_KEY) || 'null');
  } catch {
    return null;
  }
};

export default function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const messagesEndRef = useRef(null);
  const previousMessagesRef = useRef([]);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Initialize from URL params, then localStorage
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const urlUserId = searchParams.get('user');
    const urlUserName = searchParams.get('name');
    
    if (urlUserId && urlUserName) {
      // URL takes priority
      setSelectedUserId(urlUserId);
      setSelectedUserName(decodeURIComponent(urlUserName));
      setNewMessage(getDraft(urlUserId));
      saveSelectedConversation(urlUserId, decodeURIComponent(urlUserName));
    } else {
      // Check localStorage for last conversation
      const saved = getSelectedConversation();
      if (saved && saved.userId && saved.userName) {
        setSelectedUserId(saved.userId);
        setSelectedUserName(saved.userName);
        setNewMessage(getDraft(saved.userId));
        setSearchParams({ user: saved.userId, name: saved.userName });
      }
    }
  }, [searchParams, setSearchParams]);

  // Save draft when typing
  useEffect(() => {
    if (selectedUserId) {
      saveDraft(selectedUserId, newMessage);
    }
  }, [newMessage, selectedUserId]);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API}/messages/conversations`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchMessages = useCallback(async (userId) => {
    if (!user) return;
    try {
      const response = await fetch(`${API}/messages/${userId}`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [user]);

  const markConversationRead = useCallback(async (userId) => {
    if (!user) return;
    try {
      await fetch(`${API}/messages/${userId}/read`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, [user, fetchConversations]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      // Poll for new conversations every 5 seconds
      const interval = setInterval(fetchConversations, 5000);
      return () => clearInterval(interval);
    }
  }, [user, fetchConversations]);

  useEffect(() => {
    if (selectedUserId && user) {
      fetchMessages(selectedUserId);
      markConversationRead(selectedUserId);
      const interval = setInterval(() => fetchMessages(selectedUserId), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedUserId, user, fetchMessages, markConversationRead]);

  useEffect(() => {
    // Check if messages have actually changed before scrolling
    const messagesChanged = JSON.stringify(messages) !== JSON.stringify(previousMessagesRef.current);
    if (messagesChanged && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      previousMessagesRef.current = messages;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !selectedUserId) return;
    setSending(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ recipient_id: selectedUserId, content: newMessage.trim() })
      });
      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        saveDraft(selectedUserId, ''); // Clear draft after sending
        fetchConversations(); // Refresh conversation list
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = async (messageId) => {
    if (!editContent.trim()) return;
    try {
      const response = await fetch(`${API}/messages/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content: editContent.trim() })
      });
      if (response.ok) {
        const updatedMessage = await response.json();
        setMessages(prev => prev.map(msg => 
          msg.message_id === messageId ? updatedMessage : msg
        ));
        setEditingMessageId(null);
        setEditContent('');
        toast.success('Message updated');
      }
    } catch (error) {
      console.error('Failed to edit message:', error);
      toast.error('Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const response = await fetch(`${API}/messages/${messageId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        setMessages(prev => prev.filter(msg => msg.message_id !== messageId));
        toast.success('Message deleted');
        fetchConversations(); // Refresh conversation list
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleSelectConversation = (conv) => {
    const otherUser = conv.other_user;
    setSelectedUserId(otherUser.user_id);
    setSelectedUserName(otherUser.name);
    setMessages([]);
    setNewMessage(getDraft(otherUser.user_id)); // Load draft for this conversation
    setSearchParams({ user: otherUser.user_id, name: otherUser.name });
    saveSelectedConversation(otherUser.user_id, otherUser.name);
  };

  const handleBack = () => {
    // Don't clear selectedUserId completely - just hide the panel on mobile
    // Draft and conversation are preserved
    setSearchParams({});
    saveSelectedConversation(null, null);
    setSelectedUserId(null);
    setSelectedUserName('');
    setMessages([]);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  // If user is not logged in, show login prompt
  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-xl p-8">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign in to view messages</h2>
          <p className="text-gray-500 mb-6">You need to be logged in to send and receive direct messages</p>
          <Button onClick={() => navigate('/login?mode=signup')} className="bg-indigo-600 hover:bg-indigo-700">
            <LogIn className="w-4 h-4 mr-2" />
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Calculate total unread DMs for display
  const totalUnreadDMs = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  // Mobile: Show either conversation list or messages
  // Desktop: Show both side by side
  return (
    <div className="flex flex-col md:flex-row gap-4 h-[calc(100vh-12rem)] md:h-[calc(100vh-10rem)]">
      {/* Conversations List */}
      <div className={`${selectedUserId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 bg-white rounded-xl border border-gray-200/50 overflow-hidden shrink-0`}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Messages</h2>
          {totalUnreadDMs > 0 && (
            <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
              {totalUnreadDMs} unread
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-100 rounded w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No conversations yet</p>
              <p className="text-xs text-gray-400 mt-1">Start a chat from someone's post</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {conversations.map((conv) => {
                const otherUser = conv.other_user;
                const isSelected = selectedUserId === otherUser.user_id;
                return (
                  <button
                    key={conv.conversation_id || otherUser.user_id}
                    onClick={() => handleSelectConversation(conv)}
                    data-testid={`conversation-${otherUser.user_id}`}
                    className={`w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium shrink-0">
                      {getInitials(otherUser.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 text-sm truncate">{otherUser.name}</div>
                      <div className="text-xs text-gray-500 truncate">{conv.last_message?.content || 'Start chatting'}</div>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="w-5 h-5 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center shrink-0">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Messages Panel */}
      <div className={`${selectedUserId ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-white rounded-xl border border-gray-200/50 overflow-hidden`}>
        {selectedUserId ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <button onClick={handleBack} className="md:hidden p-1 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                {getInitials(selectedUserName)}
              </div>
              <div>
                <div className="font-medium text-gray-900">{selectedUserName}</div>
                <div className="text-xs text-gray-500">Direct Message</div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No messages yet</p>
                    <p className="text-xs text-gray-400">Say hello!</p>
                  </div>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.sender_id === user?.user_id;
                  const isEditing = editingMessageId === msg.message_id;
                  
                  return (
                    <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
                      <div className={`max-w-[75%] relative ${isMe ? 'order-2' : ''}`}>
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleEditMessage(msg.message_id)}
                              className="text-sm"
                              autoFocus
                            />
                            <button 
                              onClick={() => handleEditMessage(msg.message_id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => { setEditingMessageId(null); setEditContent(''); }}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className={`px-4 py-2 rounded-2xl ${
                              isMe 
                                ? 'bg-indigo-600 text-white rounded-br-md' 
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }`}>
                              <p className="text-sm">{msg.content}</p>
                              {msg.edited && (
                                <span className={`text-[10px] ${isMe ? 'text-indigo-200' : 'text-gray-400'}`}>(edited)</span>
                              )}
                            </div>
                            {/* Edit/Delete buttons - only for own messages */}
                            {isMe && (
                              <div className="absolute -left-16 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1">
                                <button 
                                  onClick={() => { setEditingMessageId(msg.message_id); setEditContent(msg.content); }}
                                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-gray-100 rounded"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteMessage(msg.message_id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-100">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="dm-input"
                />
                <Button 
                  onClick={handleSendMessage} 
                  disabled={!newMessage.trim() || sending}
                  className="bg-indigo-600 hover:bg-indigo-700 px-4"
                  data-testid="dm-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Select a conversation</p>
              <p className="text-xs text-gray-400 mt-1">or start one from a post</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
